import type React from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { useIntl } from 'react-intl';

import HeaderBackgroundImage from 'app/assets/leads-guide/header-background.svg';
import CloseIcon from 'app/assets/close-icon.svg';
import { GUIDE_STEP_COUNT, GUIDE_STEP_TITLE_IDS } from 'app/features/leads/components/guide/constants';
import { GuideFooter } from 'app/features/leads/components/guide/guide-footer';
import { GuideStepTabs } from 'app/features/leads/components/guide/guide-step-tabs';
import { HowItWorksStep } from 'app/features/leads/components/guide/steps/how-it-works-step';
import { LeadsStep } from 'app/features/leads/components/guide/steps/leads-step';
import { ProfileStep } from 'app/features/leads/components/guide/steps/profile-step';

const FOCUSABLE_SELECTOR =
  'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

type LeadsGuideModalProps = {
  isOpen: boolean;
  onClose: () => void;
};

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background-color: ${({ theme }) => `${theme.semantic.brand_950}cc`};
  z-index: 9999;
  display: flex;
  align-items: stretch;
  justify-content: center;
  padding: 0;

  ${({ theme: { media, spacing } }) =>
    media.medium(
      () => `
      align-items: center;
      padding: ${spacing(4)};
    `,
    )}
`;

const HEADER_BACKGROUND_HEIGHT = '14.0625rem';

const Dialog = styled.dialog`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  max-width: 100%;
  height: 100%;
  max-height: 100%;
  overflow: hidden;
  border-radius: 0;
  border: none;
  background-color: ${({ theme }) => theme.semantic.brand_950};
  box-shadow: none;
  padding: 0 0 ${({ theme: { spacing } }) => spacing(5)};

  ${({ theme: { media, spacing, semantic } }) =>
    media.medium(
      () => `
      max-width: 41.25rem;
      height: auto;
      max-height: 90vh;
      background-color: ${semantic.neutrals_100};
      border-radius: 0.75rem;
      border: 0.0625rem solid ${semantic.neutrals_400};
      box-shadow: 0 0.25rem 0.5rem ${semantic.brand_950}1f;
      padding: 0 0 ${spacing(5)};
    `,
    )}
`;

const DialogHeader = styled.header`
  position: relative;
  width: 100%;
  flex-shrink: 0;

  ${({ theme: { media, spacing } }) =>
    media.medium(
      () => `
      padding-top: ${spacing(16)};
    `,
    )}
`;

const HeaderTopBar = styled.div`
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  align-items: center;
  width: 100%;
  padding: ${({ theme: { spacing } }) => `${spacing(3)} ${spacing(4)}`};
  box-sizing: border-box;

  ${({ theme: { media, spacing } }) =>
    media.medium(
      () => `
      display: block;
      padding: 0;
      margin-bottom: ${spacing(5)};
    `,
    )}
`;

const HeaderBarSpacer = styled.div`
  min-height: 1.5rem;

  ${({ theme: { media } }) => media.medium(() => 'display: none;')}
`;

const HeaderBackground = styled.div<{ $src: string }>`
  display: none;
  pointer-events: none;
  z-index: 0;

  ${({ theme: { media }, $src }) =>
    media.medium(
      () => `
      display: block;
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      width: 100%;
      bottom: 0;
      min-height: ${HEADER_BACKGROUND_HEIGHT};
      background-image: url(${$src});
      background-size: cover;
      background-position: center top;
      background-repeat: no-repeat;
    `,
    )}
`;

const HeaderContent = styled.div`
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${({ theme: { spacing } }) => spacing(4)};
  width: 100%;
  padding: ${({ theme: { spacing } }) => `${spacing(8)} ${spacing(6)} 0`};
  box-sizing: border-box;

  ${({ theme: { media, spacing } }) =>
    media.medium(
      () => `
      gap: ${spacing(5)};
      padding: 0 ${spacing(8)};
    `,
    )}
`;

const DialogBody = styled.div`
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  min-height: 0;
  flex: 1;
  padding: 0 ${({ theme: { spacing } }) => spacing(6)};
  box-sizing: border-box;

  ${({ theme: { media, spacing } }) =>
    media.medium(
      () => `
      padding: 0 ${spacing(8)};
    `,
    )}
`;

const CloseButton = styled.button`
  grid-column: 3;
  justify-self: end;
  width: 1.5rem;
  height: 1.5rem;
  background: transparent;
  border: none;
  cursor: pointer;
  z-index: 2;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  img {
    width: 100%;
    height: 100%;
  }

  &:focus-visible {
    outline: 0.125rem solid ${({ theme }) => theme.semantic.accent_500};
    outline-offset: 0.125rem;
  }

  ${({ theme: { media, spacing } }) =>
    media.medium(
      () => `
      position: absolute;
      top: ${spacing(4)};
      right: ${spacing(8)};
      grid-column: unset;
      justify-self: unset;
    `,
    )}
`;

const DialogTitle = styled.h3`
  grid-column: 2;
  position: relative;
  z-index: 1;
  font-family: ${({ theme }) => theme.typography.poppins};
  font-weight: 600;
  font-size: ${({ theme }) => theme.typography.xsmall};
  line-height: 1.125rem;
  letter-spacing: 0.125rem;
  text-transform: uppercase;
  color: ${({ theme }) => theme.semantic.text_small_elements};
  text-align: center;
  margin: 0;
  padding: 0;
  white-space: nowrap;

  ${({ theme: { media, typography, spacing } }) =>
    media.medium(
      () => `
      grid-column: unset;
      width: 100%;
      box-sizing: border-box;
      font-size: ${typography.semixxl};
      font-weight: 600;
      line-height: ${typography.lineHeight};
      letter-spacing: ${typography.lsMedium};
      text-transform: none;
      text-align: left;
      white-space: normal;
      padding: 0 ${spacing(8)};
    `,
    )}
`;

const PanelScroll = styled.div`
  width: 100%;
  overflow-y: auto;
  flex: 1;
  min-height: 0;
  padding-top: ${({ theme: { spacing } }) => spacing(4)};

  ${({ theme: { media, spacing } }) =>
    media.medium(
      () => `
      padding-top: ${spacing(2)};
    `,
    )}
`;

const StepPanel = styled.div`
  width: 100%;

  &[hidden] {
    display: none;
  }
`;

const renderStepPanel = (step: number) => {
  switch (step) {
    case 0:
      return <HowItWorksStep />;
    case 1:
      return <ProfileStep />;
    case 2:
      return <LeadsStep />;
    default:
      return null;
  }
};

export const LeadsGuideModal: React.FC<LeadsGuideModalProps> = ({ isOpen, onClose }) => {
  const intl = useIntl();
  const dialogRef = useRef<HTMLDialogElement>(null);
  const previousFocusRef = useRef<HTMLElement | null>(null);
  const [currentStep, setCurrentStep] = useState(0);

  const closeAriaLabel = intl.formatMessage({ id: 'leadsGuide.closeAriaLabel' });
  const title = intl.formatMessage({ id: GUIDE_STEP_TITLE_IDS[currentStep] });
  const titleId = 'leads-guide-dialog-title';

  const handleClose = useCallback(() => {
    onClose();
    setCurrentStep(0);
  }, [onClose]);

  const handleBack = useCallback(() => {
    setCurrentStep((step) => Math.max(0, step - 1));
  }, []);

  const handleNext = useCallback(() => {
    setCurrentStep((step) => Math.min(GUIDE_STEP_COUNT - 1, step + 1));
  }, []);

  const handleOverlayClick = useCallback(
    (event: React.MouseEvent) => {
      if (event.target === event.currentTarget) {
        handleClose();
      }
    },
    [handleClose],
  );

  const wasOpenRef = useRef(false);

  useEffect(() => {
    if (isOpen && !wasOpenRef.current) {
      setCurrentStep(0);
    }
    wasOpenRef.current = isOpen;
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    previousFocusRef.current = document.activeElement as HTMLElement | null;

    const focusDialog = () => {
      const dialog = dialogRef.current;
      if (!dialog) {
        return;
      }
      const focusable = dialog.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR);
      focusable[0]?.focus();
    };

    const frameId = requestAnimationFrame(focusDialog);

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        handleClose();
        return;
      }

      if (event.key !== 'Tab' || !dialogRef.current) {
        return;
      }

      const focusable = Array.from(dialogRef.current.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR));
      if (focusable.length === 0) {
        return;
      }

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      cancelAnimationFrame(frameId);
      document.removeEventListener('keydown', handleKeyDown);
      previousFocusRef.current?.focus();
    };
  }, [isOpen, handleClose]);

  if (!isOpen) {
    return null;
  }

  return (
    <Overlay onClick={handleOverlayClick} data-testid="leads-guide-overlay">
      <Dialog
        ref={dialogRef}
        open={true}
        aria-modal="true"
        aria-labelledby={titleId}
        data-testid="leads-guide-modal"
        onClick={(event) => event.stopPropagation()}
      >
        <DialogHeader>
          <HeaderBackground
            $src={HeaderBackgroundImage}
            aria-hidden={true}
            data-testid="leads-guide-header-background"
          />
          <HeaderTopBar>
            <HeaderBarSpacer aria-hidden={true} />
            <DialogTitle id={titleId}>{title}</DialogTitle>
            <CloseButton
              type="button"
              onClick={handleClose}
              aria-label={closeAriaLabel}
              data-testid="leads-guide-close"
            >
              <img src={CloseIcon} alt="" />
            </CloseButton>
          </HeaderTopBar>
          <HeaderContent>
            <GuideStepTabs currentStep={currentStep} onStepSelect={setCurrentStep} />
          </HeaderContent>
        </DialogHeader>
        <DialogBody>
          <PanelScroll>
            {Array.from({ length: GUIDE_STEP_COUNT }, (_, index) => (
              <StepPanel
                key={GUIDE_STEP_TITLE_IDS[index]}
                role="tabpanel"
                id={`leads-guide-panel-${index}`}
                aria-labelledby={`leads-guide-tab-${index}`}
                hidden={index !== currentStep}
                tabIndex={index === currentStep ? 0 : -1}
              >
                {index === currentStep && renderStepPanel(index)}
              </StepPanel>
            ))}
          </PanelScroll>
          <GuideFooter currentStep={currentStep} onBack={handleBack} onNext={handleNext} onDone={handleClose} />
        </DialogBody>
      </Dialog>
    </Overlay>
  );
};
